import type { ThreadMessage } from "@assistant-ui/react";
import type { AgentConfig, Contact, Lead, LeadChange } from "../leads/types";
import { SERVER_SKU_PROMPT } from "../leads/hardware";
import { createEmptyLead } from "../leads/utils";
import {
  directionLabels,
  formatPrice,
  formatQuantity,
  getFreshness,
} from "../leads/utils";
import { normalizeParsedLead, parseLocally } from "./parser";
import { prepareImageForModel } from "./imagePreprocessing";
import type {
  AgentAction,
  AgentResult,
  ChangeReceipt,
  ContactChange,
} from "./types";

const STATUSES: Lead["status"][] = [
  "draft",
  "active",
  "locked",
  "closed",
  "done",
  "archived",
];

export type AgentMode = "chat" | "work";

const SYSTEM_PROMPT = `你是大宗物品交易团队的盘口 Agent。把买卖双方发来的杂乱消息整理成结构化盘口数据，也能回答业务问题。用推理理解上下文和消息的时间线，不要机械套规则；不确定的写入 pending，绝不编造。

只输出 JSON，不要 Markdown：
{"reasoning":"简要分析过程，可为空","reply":"简洁中文回答","actions":[{"type":"create","lead":{...}},{"type":"update","leadId":"已有盘口ID","changes":{...}},{"type":"delete","leadId":"..."}],"contacts":[{"name":"姓名","role":"seller/buyer/both","info":"微信号/电话","company":"公司"}],"pending":["仍待确认的信息"]}
没有数据变更时 actions=[] 且 contacts=[]。update/delete 只能使用给出的真实 leadId。

字段：direction(sell/buy), status(draft/active/locked/closed/done/archived), category, brand, sku, model, configuration, memory_capacity, quantity, unit, price, currency(CNY/USD/USDT), estimated_fee_per_unit, price_basis, availability(stock/transit/presale/unknown), location, available_at, valid_until, payment_terms, inspection, transaction_steps(string[]), company, contact_name, contact_info, seller_brokers([{name, contact_info}]), buyer_brokers([{name, contact_info}]), inquiries([{id?, buyer_name, contact_info, offer, status, note}]), source_channel, raw_text, notes, ai_confidence。

业务约定（理解语义后执行）：
- 一个盘口可绑定四个角色：货主（货源方）、买主（采购方）、卖家负责人、买家负责人。后两者是居间/中介，可以叠加多层：seller_brokers / buyer_brokers 数组按层级排列，一线（直接对接货主或买主的人）在前，每项 {name, contact_info}。contact_name 填货主或买主本人，owner 是内部跟进人，三者不要混淆。
- 一个货盘可同时被多个买家跟进：inquiries 数组每人一条（买家本人，不是居间）。status：talking（在谈/来问）/ offered（已出价）/ won（货已出给他）/ lost（明确不搞了）。「20号A问、26号B和C问、27号出给了C」这类时间线：A=talking，B=talking，C=won；其他在谈的可视情况标 lost，时间细节写进该买家的 note。update 时 inquiries 传完整数组（保留原 id），不要丢已有人。
- 报价默认指每台单价，「万/w」=×10000，price 填换算后绝对数值；只有原文明确是总价时才留空并记入 price_basis。行情消息常见「2085+10」「1970+100」「1850+100+20=1970」写法：第一个数是每台单价填 price；其后各项之和是居间费/小合同费（万/台）填 estimated_fee_per_unit；原始表达式记入 price_basis。「大合同1980，小合同30」同理（price=19800000，estimated_fee_per_unit=300000）。「卖返/买返」是返点方向，记入 price_basis 或 notes，不要加进居间费。
- B300/H200 是 GPU 平台：model 写「B300 3T」这类平台+内存；sku 只填原文出现的官方型号（目录：${SERVER_SKU_PROMPT}）；8 卡、整机、HGX 等固定参数不必重复记录。
- status 语义：已卖/没货 → closed；只剩新型号 → 旧盘 closed、新盘另建。
- 同一地区+型号+内存+方向的后续消息视为同一盘口的更新，覆盖旧值；覆盖时价格、数量、流程等所有字段都以时间线最新一条消息为准，不得新旧混搭（如旧价配新数量），也不得把旧值保留成另一张卡（同一联系人同一型号同一方向全程只能一张卡）；只有价格/数量变化明确属于新报价（不同批次、不同条件）时才拆成两个盘口。卖方后报的剩余数量优先于先报的总量；不同维度拆成不同盘口。
- 方向按动词判断：出、卖、现货出、供应=sell；收、求、找、要、需求、有没有…资源、buy=buy。「出几台」是卖不是买。pending 只放原文没有的信息；原文已明确的品牌、内存、数量、价格必须直接写进卡片对应字段，不得用 pending 代替填写。
- 现货 48 小时有效，求购 72 小时；相对时间基于当前时间。
- 批处理群聊截图时逐条消息扫描：凡含型号/数量/价格的买卖信息都必须建卡，一条不漏。H200、二手、液冷、散货、单台（如「一台H200」）、期货/预售（周期+定金）都是有效盘口；买家喊话（收、需求、有没有…资源、预算X以内、对赌）按 buy 方向建卡。群截图通常有数十条消息、十几条有效盘口，漏报比多报更糟；拿不准的先进 pending 也不要丢。只有一句「有没有人要X」没有任何数量价格等信息的喊话不足以建卡，写进 pending 即可。
- 「1870大+120+50小」这类写法：大合同数才是每台单价（price=18700000），小合同各项之和是居间费（estimated_fee_per_unit=1700000）；标题里的总数（如「价格2040」）不要当 price，只记进 price_basis。数量、价格原文没有就 null，禁止填 0。brand、型号、内存只按原文写，不得替原文升级或猜测（原文「3T戴尔」就写戴尔 B300 3T）。raw_text 必须逐字引用原文，禁止补写原文没有的内容。
- 截图/消息出现群名等来源信息且不一致或部分缺失时，不要静默假设同一来源：在 pending 里主动询问「这些截图是否都来自同一个群？要不要按群标记来源」，已确认的来源填 source_channel。
- 原文「超威」「超微」「超微電子」都指 Supermicro，brand 统一写「超微」；「戴尔」「Dell」统一写「戴尔」；昵称前缀如「微信1群 」「B300群-」「芯片&服务 」只是群名片修饰，contact_name 用本人姓名部分（如「诗诗」「羽澄」「刘磊」）。
- 图片里的文字也要读；长截图会按原始顺序切成多段，段首标“长截图第 N 段”，全部读完再按时间线合并。
- 用户消息里 〔用户引用的之前消息〕 块是用户显式引用的历史消息原文，「这条／那个」等指代以它为准。
- 消息是纯联系人信息（微信名片、昵称+微信号列表、通讯录或聊天截图，没有买卖标的）时：actions 留空，把人写进顶层 contacts 数组，每人 {name, role, info, company}。role 按语义判断：手里有货/出货方=seller，要货/采购方=buyer，两边都做=both，判断不了就留空字符串。info 放微信号、电话等联系方式，原文没有就留空。同名的人只写一条。处理含盘口的聊天/截图时禁止把群成员批量写进 contacts：contacts 只留给纯联系人消息；盘口里的对接人写进对应卡片的 contact_name / contact_info。`;

function stripJsonFence(content: string): string {
  return content
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/, "");
}

function extractJson(content: string): string {
  const stripped = stripJsonFence(content);
  const start = stripped.indexOf("{");
  const end = stripped.lastIndexOf("}");
  if (start >= 0 && end > start) return stripped.slice(start, end + 1);
  return stripped;
}

function validStatus(value: unknown, fallback: Lead["status"]): Lead["status"] {
  return STATUSES.includes(value as Lead["status"])
    ? (value as Lead["status"])
    : fallback;
}

function localSpecialUpdate(
  text: string,
  leads: Lead[],
  owner: string,
): AgentResult | null {
  if (
    !/已卖|已售|賣了|卖了|只剩|只剩下/.test(text) ||
    !/4\s*T/i.test(text) ||
    !/3\s*T/i.test(text)
  )
    return null;

  const original = createEmptyLead("sell", owner);
  original.status = "closed";
  original.brand = "技嘉";
  original.sku = "B300";
  original.model = "B300 4T";
  original.configuration = "4T 版本";
  original.memory_capacity = "4T";
  original.quantity = 128;
  original.currency = "USDT";
  original.price = null;
  original.price_basis = "原文：230 萬 USDT（總價／單價待確認）";
  original.location = "馬來西亞 · 槟城";
  original.payment_terms = "驗資（飄 1.2 USDT）；現場驗貨付款（驗一付一）";
  original.inspection = "現場驗貨，驗一付一";
  original.transaction_steps = [
    "验资（飘 1.2 USDT）",
    "现场验货",
    "付款（验一付一）",
  ];
  original.contact_name = "A 货主";
  original.source_channel = "Agent 对话";
  original.raw_text = text;
  original.notes = "周六反馈原 4T 已售，关闭旧盘口；保留周五原始信息。";

  const replacement = createEmptyLead("sell", owner);
  replacement.status = "draft";
  replacement.brand = "技嘉";
  replacement.sku = "B300";
  replacement.model = "B300 3T";
  replacement.configuration = "3T 版本";
  replacement.memory_capacity = "3T";
  replacement.quantity = null;
  replacement.price = null;
  replacement.currency = "USDT";
  replacement.price_basis = "价格待确认";
  replacement.location = "馬來西亞 · 槟城";
  replacement.contact_name = "A 货主";
  replacement.source_channel = "Agent 对话";
  replacement.raw_text = text;
  replacement.notes =
    "由原 4T 已售的跟进信息新建；3T 数量、价格和交付时间待确认。";

  const sameParty = (lead: Lead) =>
    lead.contact_name.replace(/\s/g, "") === "A货主";
  const existing4T = leads.find(
    (lead) =>
      sameParty(lead) &&
      /4\s*T/i.test(`${lead.model} ${lead.configuration}`) &&
      lead.status !== "closed",
  );
  const existing3T = leads.find(
    (lead) =>
      sameParty(lead) &&
      /3\s*T/i.test(`${lead.model} ${lead.configuration}`) &&
      !["closed", "done"].includes(lead.status),
  );
  const actions: AgentAction[] = [
    existing4T
      ? {
          type: "update",
          leadId: existing4T.id,
          changes: {
            status: "closed",
            notes:
              `${existing4T.notes}\n周六反馈已售；跟进原文：${text}`.trim(),
          },
        }
      : {
          type: "create",
          lead: original as unknown as Record<string, unknown>,
        },
  ];
  if (!existing3T)
    actions.push({
      type: "create",
      lead: replacement as unknown as Record<string, unknown>,
    });

  return {
    reply: "已按“旧盘关闭、新盘另建”处理这次版本变化。",
    actions,
    contacts: [],
    pending: ["3T 的数量、价格和交付时间", "原 230 萬 USDT 是总价还是单价"],
  };
}

export function runLocalAgent(
  text: string,
  leads: Lead[],
  owner: string,
  hasImages = false,
  mode: AgentMode = "work",
): AgentResult {
  if (hasImages) {
    return {
      reply:
        "已收到图片和文字，但本地规则不能读取图片内容，因此没有添加或修改盘口。请在 Agent 设置中连接支持视觉的模型后重新发送。",
      actions: [],
      contacts: [],
      pending: ["图片及随附文字尚未解析"],
    };
  }

  const special =
    mode === "work" ? localSpecialUpdate(text, leads, owner) : null;
  if (special) return { ...special, contacts: [] };

  if (
    /出售|求购|采购|现货|有货|供应|貨主|货主|價格|价格|USDT|\bB300\b|\bH100\b|\bH200\b/i.test(
      text,
    )
  ) {
    const drafts = parseLocally(text, owner);
    return {
      reply:
        mode === "chat"
          ? `对话模式识别到 ${drafts.length} 条买卖信息，仅供讨论，没有写入盘口。`
          : `已识别 ${drafts.length} 条买卖信息并写入盘口。`,
      actions:
        mode === "chat"
          ? []
          : drafts.map((lead) => ({
              type: "create",
              lead: lead as unknown as Record<string, unknown>,
            })),
      contacts: [],
      pending: ["请核对型号、价格口径、数量和交割条件"],
    };
  }

  const current = leads.filter((lead) =>
    ["fresh", "expiring"].includes(getFreshness(lead)),
  );
  const sell = current.filter((lead) => lead.direction === "sell").length;
  const buy = current.filter((lead) => lead.direction === "buy").length;
  const expiring = current.filter(
    (lead) => getFreshness(lead) === "expiring",
  ).length;
  return {
    reply: `当前有 ${sell} 条有效出售、${buy} 条有效求购，${expiring} 条需要重新核验。你可以直接粘贴新消息，或告诉我“把某条盘口关闭”。`,
    actions: [],
    contacts: [],
    pending: [],
  };
}

type ModelMessagePart =
  | { type: "text"; text: string }
  | { type: "image_url"; image_url: { url: string } };

async function appendImage(parts: ModelMessagePart[], source: string) {
  const images = await prepareImageForModel(source);
  if (images.length > 1) {
    parts.push({
      type: "text",
      text: `下面是一张超长聊天截图，已按原始从上到下顺序切成 ${images.length} 段；请全部读取后按时间线合并。`,
    });
  }
  for (const image of images) {
    parts.push({ type: "image_url", image_url: { url: image } });
  }
}

async function messageForModel(
  message: ThreadMessage,
): Promise<Record<string, unknown> | null> {
  if (message.role === "system") return null;
  const parts: ModelMessagePart[] = [];
  if (message.role === "user") {
    const quote = (
      message.metadata as
        { custom?: { quote?: { text?: unknown } } } | undefined
    )?.custom?.quote;
    const quoteText =
      typeof quote?.text === "string" && quote.text.trim()
        ? quote.text.trim()
        : null;
    if (quoteText) {
      parts.push({
        type: "text",
        text: `〔用户引用的之前消息〕\n${quoteText}\n〔引用结束〕`,
      });
    }
  }
  for (const part of message.content) {
    if (part.type === "text") parts.push({ type: "text", text: part.text });
    else if (message.role === "user" && part.type === "image") {
      await appendImage(parts, part.image);
    }
  }
  if (message.role === "user") {
    for (const attachment of message.attachments ?? []) {
      for (const part of attachment.content) {
        if (part.type === "image") await appendImage(parts, part.image);
        else if (part.type === "text")
          parts.push({ type: "text", text: part.text });
      }
    }
  }
  if (!parts.length) return null;
  if (parts.every((part) => part.type === "text")) {
    return {
      role: message.role,
      content: parts.map((part) => part.text).join("\n"),
    };
  }
  return { role: message.role, content: parts };
}

export async function runRemoteAgent(
  messages: readonly ThreadMessage[],
  leads: Lead[],
  owner: string,
  config: AgentConfig,
  abortSignal: AbortSignal,
  mode: AgentMode = "work",
): Promise<AgentResult> {
  const endpoint = `${config.baseUrl.replace(/\/$/, "")}/chat/completions`;
  const controller = new AbortController();
  const abort = () => controller.abort();
  abortSignal.addEventListener("abort", abort, { once: true });
  const timer = window.setTimeout(() => controller.abort(), 120_000);
  const leadContext = leads.slice(0, 100).map((lead) => ({
    id: lead.id,
    direction: lead.direction,
    status: lead.status,
    brand: lead.brand,
    sku: lead.sku,
    model: lead.model,
    configuration: lead.configuration,
    memory_capacity: lead.memory_capacity,
    quantity: lead.quantity,
    unit: lead.unit,
    price: lead.price,
    currency: lead.currency,
    estimated_fee_per_unit: lead.estimated_fee_per_unit,
    transaction_steps: lead.transaction_steps,
    location: lead.location,
    company: lead.company,
    contact_name: lead.contact_name,
    seller_brokers: lead.seller_brokers,
    buyer_brokers: lead.buyer_brokers,
    inquiries: lead.inquiries,
    valid_until: lead.valid_until,
    updated_at: lead.updated_at,
  }));
  try {
    const modelMessages = (
      await Promise.all(messages.map(messageForModel))
    ).filter((message): message is Record<string, unknown> => Boolean(message));
    const baseSystem =
      mode === "chat"
        ? `${SYSTEM_PROMPT}\n当前是对话模式：只分析、回答和讨论，严禁返回任何 create、update 或 delete 操作，actions 必须始终为 []。`
        : `${SYSTEM_PROMPT}\n当前是工作模式：用户要求整理或变更盘口时，返回必要且准确的操作。`;
    // 上游偶尔无视 JSON 模式输出散文：解析失败时带提醒重试一次
    let lastParseError: SyntaxError | null = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: config.model,
          temperature: 0.1,
          response_format: { type: "json_object" },
          messages: [
            {
              role: "system",
              content:
                attempt === 0
                  ? baseSystem
                  : `${baseSystem}\n重要：你上次没有按要求输出。现在只输出 JSON 对象本身，不要输出任何其他文字、解释或 Markdown。`,
            },
            {
              role: "user",
              content: `当前时间：${new Date().toISOString()}\n默认负责人：${owner}\n当前盘口：${JSON.stringify(leadContext)}`,
            },
            ...modelMessages,
          ],
        }),
        signal: controller.signal,
      });
      if (!response.ok)
        throw new Error(
          `接口返回 ${response.status}：${(await response.text()).slice(0, 240)}`,
        );
      const payload = await response.json();
      const content = payload?.choices?.[0]?.message?.content;
      if (typeof content !== "string")
        throw new Error("接口未返回可解析的消息内容");
      try {
        const parsed = JSON.parse(extractJson(content)) as Partial<AgentResult>;
        return {
          reply: String(parsed.reply ?? "已处理。"),
          reasoning:
            typeof parsed.reasoning === "string" ? parsed.reasoning.trim() : "",
          actions: Array.isArray(parsed.actions) ? parsed.actions : [],
          contacts: Array.isArray(parsed.contacts)
            ? parsed.contacts
                .map((item) => ({
                  name: String(item?.name ?? "").trim(),
                  role: String(item?.role ?? "").trim(),
                  info: String(item?.info ?? "").trim(),
                  company: String(item?.company ?? "").trim(),
                }))
                .filter((item) => item.name)
            : [],
          pending: Array.isArray(parsed.pending)
            ? parsed.pending.map(String)
            : [],
        };
      } catch (error) {
        if (error instanceof SyntaxError) {
          lastParseError = error;
          continue;
        }
        throw error;
      }
    }
    throw lastParseError ?? new Error("模型返回了非标准 JSON");
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      throw new Error("Agent 响应超时或已停止，请重试");
    if (error instanceof SyntaxError)
      throw new Error("模型返回了非标准 JSON，请重试");
    throw error;
  } finally {
    window.clearTimeout(timer);
    abortSignal.removeEventListener("abort", abort);
  }
}

function describeChange(change: LeadChange): string {
  const lead = change.after ?? change.before!;
  const action =
    change.type === "create"
      ? "新增"
      : change.type === "delete"
        ? "删除"
        : change.after?.status === "closed" &&
            change.before?.status !== "closed"
          ? "关闭"
          : "修改";
  const state = change.after
    ? directionLabels[change.after.direction]
    : directionLabels[lead.direction];
  return `- **${action}**｜${state} ${lead.brand ? `${lead.brand} ` : ""}${lead.model}｜${formatQuantity(lead)}｜${formatPrice(lead)}｜${lead.location || "交割地待确认"}`;
}

export function buildChangeSummary(receipt: ChangeReceipt): string {
  const pending = receipt.pending.length
    ? `\n\n**仍待确认**\n${receipt.pending.map((item) => `- ${item}`).join("\n")}`
    : "";
  const contactLines = (receipt.contactChanges ?? [])
    .map((change) => {
      const role =
        change.after.role === "seller"
          ? "卖方"
          : change.after.role === "buyer"
            ? "买方"
            : change.after.role === "both"
              ? "买卖双边"
              : "角色待标";
      const action = change.before ? "更新联系人" : "新增联系人";
      return `- **${action}**｜${change.after.name}｜${role}${change.after.info ? `｜${change.after.info}` : ""}`;
    })
    .join("\n");
  const contactBlock = contactLines
    ? `\n\n**联系人（${receipt.contactChanges!.length} 位）**\n${contactLines}`
    : "";
  const leadCount = receipt.changes.length;
  const head = leadCount
    ? `已完成并保存 ${leadCount} 条盘口变更：\n\n${receipt.changes.map(describeChange).join("\n")}`
    : "已完成联系人整理：";
  return `${head}${contactBlock}${pending}\n\n**结果已保存。** 未点 Confirm 也默认保留；需要时点 Undo 可恢复本次变更。`;
}

export async function executeAgentResult(
  result: AgentResult,
  leads: Lead[],
  apply: (changes: LeadChange[]) => Promise<void>,
  owner: string,
  mode: AgentMode = "work",
): Promise<{ receipt: ChangeReceipt | null; text: string; reasoning: string }> {
  const pending = [...result.pending];
  const reasoning = result.reasoning?.trim() ?? "";
  if (mode === "chat") {
    const pendingText = pending.length
      ? `\n\n**仍待确认**\n${pending.map((item) => `- ${item}`).join("\n")}`
      : "";
    return {
      receipt: null,
      reasoning,
      text: `${result.reply}\n\n**对话模式：未修改任何盘口数据。**${pendingText}`,
    };
  }

  const changes: LeadChange[] = [];
  const contactChanges: ContactChange[] = [];

  if (result.contacts?.length) {
    const response = await fetch("/api/contacts/import", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contacts: result.contacts }),
    });
    if (!response.ok) {
      pending.push(`联系人导入失败：${(await response.text()).slice(0, 120)}`);
    } else {
      const payload = (await response.json()) as {
        changes: { before: Contact | null; after: Contact }[];
      };
      for (const change of payload.changes ?? []) {
        contactChanges.push({
          type: change.before ? "update" : "create",
          before: change.before,
          after: change.after,
        });
      }
    }
  }

  for (const action of result.actions) {
    if (action.type === "create" && action.lead) {
      const raw = action.lead;
      const after = normalizeParsedLead(raw, String(raw.raw_text ?? ""), owner);
      after.status = validStatus(raw.status, after.status);
      changes.push({ type: "create", before: null, after });
      continue;
    }

    const target = leads.find((lead) => lead.id === action.leadId);
    if (!target) {
      pending.push(
        `未找到要${action.type === "delete" ? "删除" : "修改"}的盘口 ${action.leadId ?? "（缺少 ID）"}，已跳过`,
      );
      continue;
    }
    if (action.type === "delete") {
      changes.push({ type: "delete", before: target, after: null });
      continue;
    }
    if (action.type === "update" && action.changes) {
      const raw = { ...target, ...action.changes } as Record<string, unknown>;
      const normalized = normalizeParsedLead(
        raw,
        String(raw.raw_text ?? target.raw_text),
        target.owner,
      );
      const after: Lead = {
        ...normalized,
        id: target.id,
        created_at: target.created_at,
        created_by: target.created_by,
        status: validStatus(action.changes.status, target.status),
      };
      changes.push({ type: "update", before: target, after });
    }
  }

  if (!changes.length && !contactChanges.length) {
    const pendingText = pending.length
      ? `\n\n**仍待确认**\n${pending.map((item) => `- ${item}`).join("\n")}`
      : "";
    return {
      receipt: null,
      reasoning,
      text: `${result.reply}\n\n**未修改任何盘口数据。**${pendingText}`,
    };
  }

  await apply(changes);
  const receipt: ChangeReceipt = {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    changes,
    contactChanges,
    pending,
  };
  return { receipt, reasoning, text: buildChangeSummary(receipt) };
}
